import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar, Clock, User, Video, Search, TrendingUp, CheckCircle, XCircle, Filter, Mail, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatearSoloFechaCaracas, formatearHoraCaracas } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PanelOperador({ user, onLogout }) {
  const navigate = useNavigate();
  const [citas, setCitas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('hoy');
  const [busqueda, setBusqueda] = useState('');
  const [showGmailModal, setShowGmailModal] = useState(false);
  const [gmailActual, setGmailActual] = useState('');
  const [nuevoGmail, setNuevoGmail] = useState('');
  const [guardandoGmail, setGuardandoGmail] = useState(false);

  useEffect(() => {
    loadEstadisticas();
    loadPerfil();
  }, []);

  useEffect(() => {
    loadCitas();
  }, [filtro, busqueda]);

  const loadPerfil = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/operador/mi-perfil`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setGmailActual(data.gmail_meet || '');
      }
    } catch (error) {
      console.error('Error loading perfil:', error);
    }
  };

  const handleGuardarGmail = async () => {
    if (!nuevoGmail.trim()) {
      toast.error('Ingresa un correo de Gmail');
      return;
    }

    if (!nuevoGmail.toLowerCase().endsWith('@gmail.com')) {
      toast.error('Debe ser un correo de Gmail (@gmail.com)');
      return;
    }

    setGuardandoGmail(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/operador/configurar-gmail?gmail=${encodeURIComponent(nuevoGmail)}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setGmailActual(nuevoGmail);
        setShowGmailModal(false);
        setNuevoGmail('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar Gmail');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setGuardandoGmail(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/operador/estadisticas`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadCitas = async () => {
    try {
      const params = new URLSearchParams({ filtro });
      if (busqueda) params.append('busqueda', busqueda);
      
      const response = await fetch(`${BACKEND_URL}/api/operador/citas?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCitas(data);
      } else {
        toast.error('Error al cargar las citas');
      }
    } catch (error) {
      console.error('Error loading citas:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      onLogout();
      toast.success('Sesión cerrada');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const citasPendientes = citas.filter(c => 
    !['cliente_retenido', 'retiro_confirmado'].includes(c.estado)
  );

  const citasCompletadas = citas.filter(c => 
    ['cliente_retenido', 'retiro_confirmado'].includes(c.estado)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <div className="text-yellow-500 text-xl font-semibold">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex justify-between items-center">
          <div>
            <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mb-2" />
            <h1 className="text-3xl font-bold text-white">Panel del Operador</h1>
            <p className="text-gray-400 mt-1">Bienvenido, {user?.name}</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setNuevoGmail(gmailActual);
                setShowGmailModal(true);
              }}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <Mail className="w-4 h-4 mr-2" />
              {gmailActual ? 'Gmail Configurado' : 'Configurar Gmail'}
            </Button>
            <Button
              onClick={() => navigate('/cambiar-password')}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              Cambiar Contraseña
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="max-w-7xl mx-auto grid md:grid-cols-5 gap-6 mb-8">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Citas Hoy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-500">{stats.citas_hoy}</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Total Citas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-500">{stats.total_citas}</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Completadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-500">{stats.citas_completadas}</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Retenidos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">{stats.clientes_retenidos}</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Tasa Retención</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">{stats.tasa_retencion}%</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros y Búsqueda */}
      <div className="max-w-7xl mx-auto mb-6">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por nombre, cédula o expediente..."
                    className="pl-10 bg-gray-700/50 border-gray-600 text-white"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Select value={filtro} onValueChange={setFiltro}>
                  <SelectTrigger className="w-[180px] bg-gray-700/50 border-gray-600 text-white">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="hoy" className="text-white hover:bg-gray-700">Hoy</SelectItem>
                    <SelectItem value="semana" className="text-white hover:bg-gray-700">Esta Semana</SelectItem>
                    <SelectItem value="mes" className="text-white hover:bg-gray-700">Este Mes</SelectItem>
                    <SelectItem value="completadas" className="text-white hover:bg-gray-700">Completadas</SelectItem>
                    <SelectItem value="todas" className="text-white hover:bg-gray-700">Todas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Pendientes y Historial */}
      <div className="max-w-7xl mx-auto">
        <Tabs defaultValue="pendientes" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gray-800/50 p-1 rounded-xl border border-gray-700">
            <TabsTrigger value="pendientes" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Clock className="w-4 h-4 mr-2" />
              Citas Pendientes ({citasPendientes.length})
            </TabsTrigger>
            <TabsTrigger value="historial" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <CheckCircle className="w-4 h-4 mr-2" />
              Historial ({citasCompletadas.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pendientes" className="mt-6">
            {citasPendientes.length > 0 ? (
              <div className="grid gap-4">
                {citasPendientes.map(expediente => (
                  <CitaCard 
                    key={expediente.id} 
                    expediente={expediente} 
                    navigate={navigate}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay citas pendientes</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="historial" className="mt-6">
            {citasCompletadas.length > 0 ? (
              <div className="grid gap-4">
                {citasCompletadas.map(expediente => (
                  <CitaCard 
                    key={expediente.id} 
                    expediente={expediente} 
                    navigate={navigate}
                    isHistorial={true}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <CheckCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay citas completadas</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Configuración de Gmail */}
      {showGmailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-800 border-gray-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-yellow-500" />
                Configurar Gmail para Google Meet
              </CardTitle>
              <p className="text-gray-400 text-sm mt-2">
                Este correo se usará como co-host en todas tus reuniones virtuales de Google Meet
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {gmailActual && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-blue-300 text-sm">
                    <strong>Gmail actual:</strong> {gmailActual}
                  </p>
                </div>
              )}

              <div>
                <label className="text-gray-300 text-sm mb-2 block">
                  Correo de Gmail *
                </label>
                <Input
                  type="email"
                  value={nuevoGmail}
                  onChange={(e) => setNuevoGmail(e.target.value)}
                  placeholder="tucorreo@gmail.com"
                  className="bg-gray-700 border-gray-600 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Debe ser un correo @gmail.com válido
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setShowGmailModal(false);
                    setNuevoGmail('');
                  }}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300"
                  disabled={guardandoGmail}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleGuardarGmail}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                  disabled={guardandoGmail}
                >
                  {guardandoGmail ? 'Guardando...' : 'Guardar Gmail'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function CitaCard({ expediente, navigate, isHistorial = false }) {
  const { solicitante, cita, numero_expediente, estado } = expediente;

  const estadoConfig = {
    'cliente_retenido': { label: 'Cliente Retenido', color: 'bg-green-500', icon: CheckCircle },
    'retiro_confirmado': { label: 'Retiro Confirmado', color: 'bg-orange-500', icon: XCircle },
  };

  const config = estadoConfig[estado];

  return (
    <Card 
      className="bg-gray-800/50 border-gray-700 hover:border-yellow-500/50 transition-all cursor-pointer"
      onClick={() => navigate(`/operador/cita/${expediente.id}`)}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-full p-2">
                <User className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">{solicitante.nombre_completo}</h3>
                <p className="text-sm text-gray-400">Expediente: {numero_expediente}</p>
              </div>
              {isHistorial && config && (
                <Badge className={`${config.color} text-white flex items-center gap-2 ml-2`}>
                  {config.icon && <config.icon className="w-3 h-3" />}
                  {config.label}
                </Badge>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Fecha</p>
                <p className="text-sm text-gray-300 font-medium">
                  {formatearSoloFechaCaracas(cita.fecha)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Hora</p>
                <p className="text-sm text-gray-300 font-medium">
                  {formatearHoraCaracas(cita.fecha)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Cédula</p>
                <p className="text-sm text-gray-300">{solicitante.cedula}</p>
              </div>
            </div>

            <div className="flex gap-2">
              {cita.meet_link && (
                <a 
                  href={cita.meet_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Video className="w-3 h-3" />
                  Meet
                </a>
              )}
            </div>
          </div>

          <div className="ml-4 flex items-center">
            <div className="text-yellow-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
